// src/components/landing/LandingPage.jsx
import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  BadgeCheck,
  Building2,
  Church,
  GraduationCap,
  Globe,
  HandHeart,
  HeartHandshake,
  Landmark,
  LocateFixed,
  Lock,
  MapPin,
  Play,
  Search,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";

import Logo from "../../img/logo.png";
import GroupImageOne from "../../img/img_1.png";
import GroupImageTwo from "../../img/img_2.png";
import GroupImageThree from "../../img/img_3.png";
import VideoFR from "../../video/video_ads.mp4";
import VideoEN from "../../video/video_ads_en.mp4";

const groupCards = [
  {
    name: "McGill Engineering Builders",
    description: "Student-led projects, study circles, and hack nights.",
    location: "Montreal",
    members: "1,248 members",
    status: "Verified",
    image: GroupImageOne,
    icon: BadgeCheck,
    tone: "bg-emerald-50 text-emerald-700",
  },
  {
    name: "Newcomers in Canada",
    description: "Local guidance, language support, and first friends.",
    location: "Canada",
    members: "8,420 members",
    status: "Public",
    image: GroupImageTwo,
    icon: Users,
    tone: "bg-sky-50 text-sky-700",
  },
  {
    name: "Black Entrepreneurs Network",
    description: "Mentorship, funding resources, and founder meetups.",
    location: "Online and local",
    members: "3,106 members",
    status: "Verified",
    image: GroupImageThree,
    icon: ShieldCheck,
    tone: "bg-violet-50 text-violet-700",
  },
  {
    name: "Parents in Laval",
    description: "After-school ideas, family events, and trusted referrals.",
    location: "Laval",
    members: "914 members",
    status: "Private",
    image: GroupImageOne,
    icon: Lock,
    tone: "bg-amber-50 text-amber-700",
  },
  {
    name: "Montreal Basketball Runs",
    description: "Pickup games, gym times, teams, and weekend runs.",
    location: "Montreal",
    members: "2,772 members",
    status: "Public",
    image: GroupImageTwo,
    icon: Users,
    tone: "bg-rose-50 text-rose-700",
  },
  {
    name: "Local Volunteer Opportunities",
    description: "Causes, shifts, community drives, and helping hands.",
    location: "Near you",
    members: "5,331 members",
    status: "Public",
    image: GroupImageThree,
    icon: HandHeart,
    tone: "bg-teal-50 text-teal-700",
  },
];

const searchExamples = [
  "McGill engineering students",
  "Basketball groups in Montreal",
  "Newcomers in Canada",
  "Black entrepreneurs",
  "Parents in Laval",
  "Local volunteer opportunities",
];

const smartTools = [
  "Recommend the group to suitable people",
  "Welcome and onboard new members",
  "Match members by interests, skills, location, or needs",
  "Summarize discussions and highlight unanswered questions",
  "Suggest events, activities, polls, and meeting times",
  "Translate communication for multilingual communities",
];

const audiences = [
  { label: "Students", icon: GraduationCap },
  { label: "Community leaders", icon: HeartHandshake },
  { label: "Clubs and associations", icon: Building2 },
  { label: "Nonprofits", icon: HandHeart },
  { label: "Neighbourhoods", icon: Landmark },
  { label: "Cultural and faith communities", icon: Church },
];

const copy = {
  en: {
    nav: {
      explore: "Explore",
      create: "Create a Group",
      login: "Log In",
    },
    hero: {
      title: "Find your people. Build something together.",
      subtitle:
        "Discover student groups, local communities and organizations, or create a smart group of your own.",
      placeholder: "Search groups, communities, schools or interests",
      search: "Search Ziihub",
      create: "Create a Group",
      explore: "Explore Groups",
    },
    examples: searchExamples,
    groups: {
      eyebrow: "Popular near you",
      title: "Communities forming right now",
      description:
        "Real activity makes the platform legible before anyone has to sign up.",
      view: "View Group",
      join: "Join",
    },
    smart: {
      eyebrow: "Smart groups",
      title:
        "Ziihub helps people find their community and helps communities organize themselves.",
      tools: smartTools,
    },
    steps: [
      ["1", "Find or create a group", "Search by interest, school, location, community, or purpose."],
      ["2", "Bring people together", "Invite members and give everyone one shared space."],
      ["3", "Let Ziihub help", "Use intelligent tools to organize communication, events, and resources."],
    ],
    audience: {
      eyebrow: "Built for many kinds of community",
      title: "Clear pathways for the people who gather others.",
      signal: "Search engine and intelligent home for communities",
      labels: [
        "Students",
        "Community leaders",
        "Clubs and associations",
        "Nonprofits",
        "Neighbourhoods",
        "Cultural and faith communities",
      ],
    },
    final: {
      title: "Your community already has a purpose. Give it a smarter home.",
      cta: "Create a Group",
    },
    footer: {
      privacy: "Privacy",
      terms: "Terms",
      cookies: "Cookies",
    },
  },
  fr: {
    nav: {
      explore: "Explorer",
      create: "Créer un groupe",
      login: "Connexion",
    },
    hero: {
      title: "Trouvez votre monde. Construisez quelque chose ensemble.",
      subtitle:
        "Découvrez des groupes étudiants, des communautés locales et des organisations, ou créez votre propre groupe intelligent.",
      placeholder: "Rechercher des groupes, communautés, écoles ou intérêts",
      search: "Rechercher sur Ziihub",
      create: "Créer un groupe",
      explore: "Explorer les groupes",
    },
    examples: [
      "Étudiants en génie à McGill",
      "Groupes de basketball à Montréal",
      "Nouveaux arrivants au Canada",
      "Entrepreneurs noirs",
      "Parents à Laval",
      "Occasions de bénévolat local",
    ],
    groups: {
      eyebrow: "Populaire près de vous",
      title: "Des communautés se forment maintenant",
      description:
        "L’activité réelle rend la plateforme claire avant même l’inscription.",
      view: "Voir le groupe",
      join: "Rejoindre",
    },
    smart: {
      eyebrow: "Groupes intelligents",
      title:
        "Ziihub aide les gens à trouver leur communauté et aide les communautés à s’organiser.",
      tools: [
        "Recommander le groupe aux bonnes personnes",
        "Accueillir et intégrer automatiquement les nouveaux membres",
        "Mettre en relation les membres selon leurs intérêts, compétences, lieu ou besoins",
        "Résumer les discussions et repérer les questions sans réponse",
        "Suggérer des événements, activités, sondages et horaires de rencontre",
        "Traduire les communications pour les communautés multilingues",
      ],
    },
    steps: [
      ["1", "Trouvez ou créez un groupe", "Recherchez par intérêt, école, lieu, communauté ou objectif."],
      ["2", "Rassemblez les gens", "Invitez les membres et donnez à tous un espace partagé."],
      ["3", "Laissez Ziihub aider", "Utilisez des outils intelligents pour organiser les communications, événements et ressources."],
    ],
    audience: {
      eyebrow: "Conçu pour plusieurs types de communautés",
      title: "Des chemins clairs pour celles et ceux qui rassemblent.",
      signal: "Moteur de recherche et maison intelligente pour les communautés",
      labels: [
        "Étudiants",
        "Leaders communautaires",
        "Clubs et associations",
        "OBNL",
        "Quartiers",
        "Communautés culturelles et religieuses",
      ],
    },
    final: {
      title: "Votre communauté a déjà un but. Donnez-lui une maison plus intelligente.",
      cta: "Créer un groupe",
    },
    footer: {
      privacy: "Confidentialité",
      terms: "Conditions",
      cookies: "Cookies",
    },
  },
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [query, setQuery] = useState("");
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  const lang = (i18n.language || "en").toLowerCase().startsWith("fr") ? "fr" : "en";
  const text = copy[lang];
  const videoSrc = lang === "fr" ? VideoFR : VideoEN;
  const videoText =
    lang === "fr"
      ? {
          button: "Voir la video",
          eyebrow: "Ziihub en action",
          title: "Voyez comment fonctionne un groupe intelligent",
          body: "Une courte presentation de la facon dont Ziihub aide les personnes a decouvrir des communautes et les organisateurs a mieux les animer.",
          close: "Fermer la video",
        }
      : {
          button: "Watch video",
          eyebrow: "Ziihub in motion",
          title: "See how a smart group works",
          body: "A short walkthrough of how Ziihub helps people discover communities and helps organizers keep everything moving.",
          close: "Close video",
        };
  const carouselItems = useMemo(() => [...groupCards, ...groupCards], []);
  const heroStats =
    lang === "fr"
      ? ["Clubs etudiants", "Groupes locaux", "OBNL", "Quartiers"]
      : ["Student clubs", "Local groups", "Nonprofits", "Neighbourhoods"];
  const localizedAudiences = audiences.map((audience, index) => ({
    ...audience,
    label: text.audience.labels[index],
  }));

  const handleSearch = (event) => {
    event.preventDefault();
    const trimmedQuery = query.trim();
    navigate(trimmedQuery ? `/auth?search=${encodeURIComponent(trimmedQuery)}` : "/auth");
  };

  const toggleLanguage = () => {
    i18n.changeLanguage(lang === "en" ? "fr" : "en");
  };

  return (
    <div className="min-h-screen bg-[#fbfcf8] text-slate-950">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 backdrop-blur">
        <nav className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3.5 sm:px-6 lg:px-8">
          <Link to="/" className="flex items-center gap-3" aria-label="Ziihub home">
            <img src={Logo} alt="Ziihub logo" className="h-9 w-9 object-contain" />
            <span className="text-lg font-extrabold tracking-tight text-slate-950">
              Ziihub
            </span>
          </Link>

          <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 sm:gap-5">
            <a href="#discover" className="hidden hover:text-teal-700 sm:inline">
              {text.nav.explore}
            </a>
            <Link to="/auth" className="hidden hover:text-teal-700 sm:inline">
              {text.nav.create}
            </Link>
            <button
              type="button"
              onClick={toggleLanguage}
              className="inline-flex items-center gap-1 rounded-md px-2 py-2 text-xs font-bold hover:bg-slate-100 hover:text-teal-700"
              aria-label="Change language"
            >
              <Globe className="h-4 w-4" aria-hidden="true" />
              {lang === "en" ? "FR" : "EN"}
            </button>
            <Link to="/auth" className="rounded-md border border-slate-300 px-3 py-2 hover:border-teal-500 hover:text-teal-700">
              {text.nav.login}
            </Link>
          </div>
        </nav>
      </header>

      <main>
        <section className="relative overflow-hidden border-b border-slate-200 bg-[linear-gradient(180deg,#ffffff_0%,#f8fbf6_62%,#eef7f2_100%)]">
          <div className="mx-auto flex min-h-[70vh] max-w-6xl flex-col items-center justify-center px-4 pb-12 pt-14 text-center sm:px-6 lg:px-8">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-teal-200 bg-white px-3 py-1.5 text-xs font-bold text-teal-800 shadow-sm">
              <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
              {lang === "fr" ? "Le moteur de recherche communautaire" : "The community search engine"}
            </div>

            <img src={Logo} alt="" className="mb-4 h-16 w-16 object-contain" />
            <h1 className="max-w-3xl text-3xl font-extrabold leading-tight tracking-normal text-slate-950 sm:text-4xl lg:text-5xl">
              {text.hero.title}
            </h1>
            <p className="mt-4 max-w-2xl text-base leading-7 text-slate-600">
              {text.hero.subtitle}
            </p>

            <form onSubmit={handleSearch} className="mt-8 w-full max-w-3xl rounded-[2rem] border border-white bg-white/80 p-3 shadow-2xl shadow-teal-900/10 ring-1 ring-slate-200/80 backdrop-blur">
              <div className="flex min-h-14 items-center gap-3 rounded-full border border-slate-300 bg-white px-4 shadow-sm focus-within:border-teal-500 focus-within:ring-4 focus-within:ring-teal-100">
                <Search className="h-5 w-5 shrink-0 text-slate-500" aria-hidden="true" />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  className="min-w-0 flex-1 bg-transparent py-3.5 text-base text-slate-950 outline-none placeholder:text-slate-400"
                  placeholder={text.hero.placeholder}
                  aria-label={text.hero.placeholder}
                />
                <button
                  type="submit"
                  className="hidden rounded-full bg-teal-600 px-5 py-2.5 text-sm font-bold text-white shadow-sm shadow-teal-700/20 hover:bg-teal-700 sm:inline-flex"
                >
                  {text.hero.search}
                </button>
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                {text.examples.slice(0, 4).map((example) => (
                  <button
                    key={example}
                    type="button"
                    onClick={() => setQuery(example)}
                    className="rounded-full border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600 hover:border-teal-400 hover:bg-teal-50 hover:text-teal-800"
                  >
                    {example}
                  </button>
                ))}
              </div>
            </form>

            <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <button
                type="button"
                onClick={handleSearch}
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-teal-600 px-6 py-3 text-sm font-bold text-white hover:bg-teal-700 sm:hidden sm:w-auto"
              >
                {text.hero.search}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </button>
              <Link
                to="/auth"
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-slate-950 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-slate-900/10 hover:bg-slate-800 sm:w-auto"
              >
                {text.hero.create}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <a
                href="#discover"
                className="inline-flex w-full items-center justify-center rounded-md border border-slate-200 bg-white px-6 py-3 text-sm font-bold text-slate-700 hover:border-teal-300 hover:text-teal-800 sm:w-auto"
              >
                {text.hero.explore}
              </a>
              <button
                type="button"
                onClick={() => setIsVideoOpen(true)}
                className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-6 py-3 text-sm font-bold text-slate-700 hover:border-teal-300 hover:text-teal-800 sm:w-auto"
              >
                <Play className="h-4 w-4 fill-current" aria-hidden="true" />
                {videoText.button}
              </button>
            </div>

            <div className="mt-8 grid w-full max-w-3xl grid-cols-2 gap-2 sm:grid-cols-4">
              {heroStats.map((item) => (
                <div key={item} className="rounded-md border border-white bg-white/70 px-3 py-3 text-xs font-bold text-slate-600 shadow-sm">
                  {item}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white px-4 py-14 sm:px-6 lg:px-8">
          <div className="mx-auto grid max-w-6xl items-center gap-8 lg:grid-cols-[0.8fr_1.2fr]">
            <div>
              <p className="text-sm font-bold uppercase tracking-wider text-teal-700">
                {videoText.eyebrow}
              </p>
              <h2 className="mt-3 text-2xl font-extrabold leading-tight tracking-normal text-slate-950 sm:text-3xl">
                {videoText.title}
              </h2>
              <p className="mt-4 max-w-lg text-sm leading-6 text-slate-600">
                {videoText.body}
              </p>
              <button
                type="button"
                onClick={() => setIsVideoOpen(true)}
                className="mt-6 inline-flex items-center gap-2 rounded-md bg-slate-950 px-5 py-3 text-sm font-bold text-white hover:bg-slate-800"
              >
                <Play className="h-4 w-4 fill-current" aria-hidden="true" />
                {videoText.button}
              </button>
            </div>

            <button
              type="button"
              onClick={() => setIsVideoOpen(true)}
              className="group relative overflow-hidden rounded-lg border border-slate-200 bg-slate-950 text-left shadow-2xl shadow-slate-200/80"
              aria-label={videoText.button}
            >
              <video
                src={videoSrc}
                className="aspect-video w-full object-cover opacity-75 transition group-hover:opacity-90"
                muted
                playsInline
                preload="metadata"
              />
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white text-slate-950 shadow-xl transition group-hover:scale-105">
                  <Play className="h-7 w-7 fill-current" aria-hidden="true" />
                </span>
              </span>
            </button>
          </div>
        </section>

        <section id="discover" className="border-y border-slate-200 bg-[#f7faf7] py-14">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-sm font-bold uppercase tracking-wider text-teal-700">
                  {text.groups.eyebrow}
                </p>
                <h2 className="mt-2 text-2xl font-extrabold tracking-normal text-slate-950 sm:text-3xl">
                  {text.groups.title}
                </h2>
              </div>
              <p className="max-w-md text-sm leading-6 text-slate-600">
                {text.groups.description}
              </p>
            </div>
          </div>

          <div className="mt-8 overflow-hidden">
            <div className="ziihub-carousel flex w-max gap-4 px-4">
              {carouselItems.map((group, index) => {
                const StatusIcon = group.icon;
                return (
                  <article
                    key={`${group.name}-${index}`}
                    className="w-[292px] shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-md shadow-slate-200/70 transition-transform hover:-translate-y-1"
                  >
                    <img src={group.image} alt="" className="h-36 w-full object-cover" />
                    <div className="space-y-4 p-4 text-left">
                      <div className="flex items-start justify-between gap-3">
                        <h3 className="text-base font-extrabold leading-5 text-slate-950">
                          {group.name}
                        </h3>
                        <span className={`inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-[11px] font-bold ${group.tone}`}>
                          <StatusIcon className="h-3.5 w-3.5" aria-hidden="true" />
                          {group.status}
                        </span>
                      </div>
                      <p className="min-h-[44px] text-sm leading-5 text-slate-600">
                        {group.description}
                      </p>
                      <div className="flex items-center justify-between gap-3 text-xs font-semibold text-slate-500">
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                          {group.location}
                        </span>
                        <span>{group.members}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Link
                          to="/auth"
                          className="rounded-md border border-slate-300 px-3 py-2 text-center text-xs font-bold text-slate-700 hover:border-teal-500 hover:text-teal-700"
                        >
                          {text.groups.view}
                        </Link>
                        <Link
                          to="/auth"
                          className="rounded-md bg-slate-950 px-3 py-2 text-center text-xs font-bold text-white hover:bg-teal-700"
                        >
                          {text.groups.join}
                        </Link>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="mx-auto grid max-w-6xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-[0.95fr_1.05fr] lg:px-8">
          <div>
            <p className="text-sm font-bold uppercase tracking-wider text-teal-700">
              {text.smart.eyebrow}
            </p>
            <h2 className="mt-3 text-2xl font-extrabold leading-tight tracking-normal text-slate-950 sm:text-3xl">
              {text.smart.title}
            </h2>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {text.smart.tools.map((tool) => (
              <div key={tool} className="flex gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm shadow-slate-100">
                <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-teal-600" aria-hidden="true" />
                <p className="text-sm font-semibold leading-6 text-slate-700">{tool}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-[linear-gradient(135deg,#10201d_0%,#13213a_55%,#2f1f46_100%)] py-16 text-white">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-4 md:grid-cols-3">
              {text.steps.map(([number, title, body]) => (
                <div key={number} className="rounded-lg border border-white/15 bg-white/10 p-5 shadow-xl shadow-black/10">
                  <div className="mb-5 flex h-9 w-9 items-center justify-center rounded-md bg-white text-sm font-extrabold text-slate-950">
                    {number}
                  </div>
                  <h3 className="text-lg font-extrabold">{title}</h3>
                  <p className="mt-3 text-sm leading-6 text-slate-300">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-sm font-bold uppercase tracking-wider text-teal-700">
                {text.audience.eyebrow}
              </p>
              <h2 className="mt-2 text-2xl font-extrabold tracking-normal text-slate-950 sm:text-3xl">
                {text.audience.title}
              </h2>
            </div>
            <span className="inline-flex items-center gap-2 text-sm font-bold text-slate-600">
              <LocateFixed className="h-4 w-4 text-teal-600" aria-hidden="true" />
              {text.audience.signal}
            </span>
          </div>

          <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {localizedAudiences.map((audience) => {
              const AudienceIcon = audience.icon;
              return (
                <Link
                  key={audience.label}
                  to="/auth"
                  className="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-4 text-left shadow-sm shadow-slate-100 hover:border-teal-400 hover:shadow-md"
                >
                  <span className="flex items-center gap-3 text-sm font-extrabold text-slate-800">
                    <AudienceIcon className="h-5 w-5 text-teal-600" aria-hidden="true" />
                    {audience.label}
                  </span>
                  <ArrowRight className="h-4 w-4 text-slate-400" aria-hidden="true" />
                </Link>
              );
            })}
          </div>
        </section>

        <section className="border-t border-slate-200 bg-[#f7faf7] px-4 py-16 text-center sm:px-6 lg:px-8">
          <h2 className="mx-auto max-w-3xl text-2xl font-extrabold leading-tight tracking-normal text-slate-950 sm:text-3xl">
            {text.final.title}
          </h2>
          <Link
            to="/auth"
            className="mt-8 inline-flex items-center justify-center gap-2 rounded-md bg-teal-600 px-7 py-3 text-sm font-bold text-white hover:bg-teal-700"
          >
            {text.final.cta}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </section>
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-col gap-4 px-4 py-8 text-sm text-slate-500 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <img src={Logo} alt="" className="h-8 w-8 object-contain" />
            <span className="font-semibold text-slate-700">Ziihub</span>
          </div>
          <div className="flex flex-wrap gap-5">
            <Link to="/privacy" className="hover:text-slate-900">{text.footer.privacy}</Link>
            <Link to="/terms" className="hover:text-slate-900">{text.footer.terms}</Link>
            <Link to="/cookies" className="hover:text-slate-900">{text.footer.cookies}</Link>
          </div>
        </div>
      </footer>

      {isVideoOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/85 p-4"
          onClick={() => setIsVideoOpen(false)}
        >
          <div
            className="relative w-full max-w-4xl rounded-lg bg-slate-950 p-3 shadow-2xl ring-1 ring-white/10"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setIsVideoOpen(false)}
              className="absolute right-3 top-3 z-10 rounded-md bg-white px-3 py-2 text-xs font-bold text-slate-950 shadow-lg hover:bg-slate-100"
            >
              {videoText.close}
            </button>
            <video
              src={videoSrc}
              className="aspect-video w-full rounded-md bg-black"
              controls
              autoPlay
              playsInline
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
